import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import {useForm} from "antd/lib/form/Form";
import {isEqual} from "lodash";

import HeaderNotifications from "./components/HeaderNotifications";
import SearchBlock from "./components/SearchBlock";
import NotificationsForm from "./components/NotificationsForm";
import {mapNotificationsData} from "./components/Colums";
import TableNotifications from "./components/TableNotifications";
import {userAC} from "../../store/branches/user/actionCreators";
import {LoadingStatus} from "../../store/status";
import Preloader from "../../components/Preloader";

import {Site} from "../../store/branches/sites/stateTypes";
import DeviceAlert from "../../components/Alerts/device";
import {selectDevicesState, selectSitesState, selectUserState} from "../../store/selectors";
import {useCurrentSelection} from "../../hooks/useCurrentSelection";

import classes from "./Notifications.module.scss";
import {sitesAC} from "../../store/branches/sites/actionCreators";
import {devicesAC} from "../../store/branches/devices/actionCreators";

interface ISorting {
    column: string;
    order: "ascend" | "descend" | false;
}

export interface IFilterData {
    date_created_from: string,
    date_created_to: string,
    location: string,
    device: string,
    event_type: string
}

const initialFilterData: IFilterData = {
    date_created_from: null,
    date_created_to: null,
    location: null,
    device: null,
    event_type: null,
};

const Notifications: React.FC = () => {
    const [form] = useForm();
    const dispatch = useDispatch();

    const [searchData, setSearchData] = useState<IFilterData>(initialFilterData);

    const {
        status,
        notifications: {results: notifications = [], count: countNotifications},
        notificationsFilter: {isActive},
    } = useSelector(selectUserState);
    const {device, site} = useCurrentSelection();


    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({page: 1, pageSize: 20});
    const [sorting, setSorting] = useState<ISorting>({column: "date_created", order: "descend"});
    const {pathname, search} = useLocation();



    useEffect(() => {
        // Check if pathname is /notifications/all and reset location/device if true
        if (pathname === "/notifications/all") {
            setSearchData(prev => ({ ...prev, location: null, device: null }));
        } else {
            setSearchData(prev => ({
                ...prev,
                location: site?.id?.toString() ?? prev.location,
                device: device?.id?.toString() ?? prev.device,
            }));
        }
    }, [pathname, search, site, device]);

    useEffect(() => {
        const data = {
            limit: pagination.pageSize,
            offset: (pagination.page - 1) * pagination.pageSize,
            search: searchTerm,
            is_active: isActive,
            ...searchData,
            ordering: `${sorting.order === "descend" ? "-" : ""}${sorting.column},-date_created`,
        }

        if(sites == null){
            dispatch(sitesAC.fetchSites());
        }
        if(devicesDate == null) {
            dispatch(devicesAC.fetchDevices());
        }
        if(sites == null){
            dispatch(sitesAC.fetchSites());
        }
        if(devicesDate == null) {
            dispatch(devicesAC.fetchDevices());
        }
        dispatch(userAC.searchNotifications(data));
        if(sites == null){
            dispatch(sitesAC.fetchSites());
        }
        if(devicesDate == null) {
            dispatch(devicesAC.fetchDevices());
        }
    }, [dispatch, sorting.order, sorting.column, searchTerm, pagination, isActive, searchData]);

    const {sitesData} = useSelector(selectSitesState);
    const {devicesDate} = useSelector(selectDevicesState);
    const sites: Site[] = sitesData?.results;

    const onChangeSearch = useCallback((value: string) => {
        setSearchTerm(value);
    }, []);

    const onChangePagination = useCallback((page: number, pageSize: number) => {
        setPagination({page, pageSize});
    }, []);

    const onChangeSort = useCallback((sorter) => {
        setSorting({order: sorter.order, column: sorter?.column?.dataIndex});
    }, []);

    const onChangeLocation = useCallback((value: any, option: any) => {
        if (option.props === "All") {
            setSearchData(prev => ({...prev, location: null}));
            return;
        }
        setSearchData(prev => ({...prev, location: option.props}));
    }, []);

    const onClearFilters = useCallback(() => {
        dispatch(userAC.setNotificationsFilter(true));
        form.resetFields();
        setSearchTerm("");
        setSearchData({
            date_created_from: null,
            date_created_to: null,
            location: null,
            device: null,
            event_type: null,
        });
    }, [form, dispatch]);

    const handSelectDate = (value: Array<any>) => {
        dispatch(userAC.setNotificationsFilter(false));
        let date_created_from: string = null;
        let date_created_to: string = null;

        if (value) {
            const [from, to] = value;
            date_created_from = from ? from.format("YYYY-MM-DD") : null;
            date_created_to = to ? to.format("YYYY-MM-DD") : null;
        }

        setSearchData(prev => ({
            ...prev,
            date_created_from,
            date_created_to,
        }));
    };

    const handSelectDevice = (value: any, option: any) => {
        if (option.props === "All") {
            setSearchData(prev => ({...prev, device: null}));
            return;
        }
        setSearchData(prev => ({...prev, device: option.props}));
    };

    const handSelectType = (value: any, option: any) => {
        if (option.props === "All") {
            setSearchData(prev => ({...prev, event_type: null}));
            return;
        }
        setSearchData(prev => ({...prev, event_type: option.props}));
    };

    const tableData = useMemo(() => {
        return mapNotificationsData(notifications || [], isActive, site?.timezone);
    }, [notifications, isActive, site?.timezone]);

    return (
        <Preloader isLoaded={status === LoadingStatus.LOADING}>
            <div className={classes.wrap}>
                <DeviceAlert/>
                <HeaderNotifications/>
                <SearchBlock onSearch={onChangeSearch}
                             value={searchTerm}
                             count={countNotifications}
                             searchPayload={{
                                 limit: pagination.pageSize,
                                 offset: (pagination.page - 1) * pagination.pageSize,
                                 search: searchTerm,
                                 is_active: isActive,
                                 ...searchData,
                                 ordering: `${sorting.order === "descend" ? "-" : ""}${sorting.column}`,
                             }}
                />

                {
                    (sites && devicesDate)
                    &&
                    <NotificationsForm form={form}
                                       searchData={searchData}
                                       onClearFilters={onClearFilters}
                                       isClearFilters={searchTerm !== "" || !isEqual(initialFilterData, searchData)}
                                       handSelectDate={handSelectDate}
                                       handSelectSite={onChangeLocation}
                                       handSelectDevice={handSelectDevice}
                                       handSelectType={handSelectType}
                                       sites={sites}
                                       devices={devicesDate}
                    />
                }

                <TableNotifications data={tableData}
                                    count={countNotifications}
                                    pageNumber={pagination.page}
                                    onPagesChange={onChangePagination}
                                    sorting={sorting}
                                    onChangeSort={onChangeSort}
                />
            </div>
        </Preloader>
    );
};

export default Notifications;
